import React, { useState, useLayoutEffect } from 'react';
import { Box, Grid, makeStyles, Typography, LinearProgress, withStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { PATH_CALENDAR, PATH_HOME } from '../../containers/RoutePaths';
import AdaptiveNavBar from '../utilities/NavBar/AdaptiveNavBar';
import { TutorialPageStyle } from '../../styles/TutorialPageStyle';
import { PrimaryButton } from '../utilities/commonUtilities/GenericButtonElements';
import { styleBreakMobile, styleBreakSmallMobile, styleBreakDesktop } from '../utilities/layouts/Globals';
import { SizeSelector } from '../utilities/layouts/SizeSelector';
import PropTypes from 'prop-types';
import { COLOR_WHITE } from '../utilities/MaterialWatchman';
import { ReactComponent as WelcomeImage } from '../../resources/images/welcome-image.svg';
import CalendarTutorial from './CalendarTutorial';
import OnboardingTutorial from './OnboardingTutorial';

const useStyles = makeStyles(theme => ({
    whiteBackground: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        background: COLOR_WHITE,
        boxShadow: `0px 1px 4px rgba(186, 186, 186, 0.25)`,
        zIndex: '9999',
    },
    tutorialProgressBar: {
        position: 'relative',
        height: theme.spacing(1.375),
        top: theme.spacing(10.625),
        [styleBreakMobile(theme)]: {
            position: 'sticky',
            top: 0,
        },
    },
    nextButton: {
        width: theme.spacing(47.5),
        [styleBreakMobile(theme)]: {
            marginLeft: theme.spacing(3),
            marginRight: theme.spacing(3),
            width: '100%',
            minWidth: theme.spacing(19.25),
        },
        [styleBreakSmallMobile(theme)]: {
            marginLeft: theme.spacing(2),
            marginRight: theme.spacing(2),
            minWidth: theme.spacing(14),
        },
        [styleBreakDesktop(theme)]: {},
    },
    backButton: {
        background: COLOR_WHITE,
        minWidth: theme.spacing(14),
        [styleBreakMobile(theme)]: {
            marginLeft: theme.spacing(3),
            width: '100%',
            minWidth: theme.spacing(19.25),
        },
        [styleBreakSmallMobile(theme)]: {
            marginLeft: theme.spacing(2),
            minWidth: theme.spacing(14),
        },
        [styleBreakDesktop(theme)]: {
            marginLeft: theme.spacing(6.5),
        },
    },
    mobileFooter: {
        position: 'fixed',
        marginBottom: theme.spacing(3),
    },
    buttonGrid: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
    },
}));

const StyledLinearProgress = withStyles({
    bar: {
        borderRadius: '5px',
    },
})(LinearProgress);

export const TUTORIAL_TYPE = { ONBOARDING: 'Onboarding', CALENDAR: 'Calendar' };

export default function TutorialWelcomePage({
    subtitleText,
    bodyText,
    buttonText = 'Start Quick Overview',
    setLaunchTutorial,
    bodyTestDataAttr,
    stylesOverride,
    hideNavbar = false,
    noOfPages,
    isMobile,
    type,
}) {
    const classes = useStyles();
    const history = useHistory();
    const [tutorialCurrentPage, setTutorialCurrentPage] = useState(1);

    const onNextPage = () => {
        if (tutorialCurrentPage !== noOfPages) setTutorialCurrentPage(tutorialCurrentPage + 1);
        else {
            type === TUTORIAL_TYPE.CALENDAR ? history.push(PATH_CALENDAR) : history.push(PATH_HOME);
            setLaunchTutorial(true);
        }
    };

    const onPreviousPage = () => {
        setTutorialCurrentPage(tutorialCurrentPage - 1);
    };

    useLayoutEffect(() => {
        setTutorialCurrentPage(1);
    }, [isMobile]);

    // Function to normalise the values. '(MIN / MAX could be integrated)'
    const MIN = 1; // MIN = Minimum expected value
    const MAX = noOfPages; // MAX = Maximium expected value
    const normalise = value => ((value - MIN) * 100) / (MAX - MIN);

    function Progress(props) {
        return (
            <React.Fragment>
                <StyledLinearProgress className={classes.tutorialProgressBar} variant="determinate" value={normalise(props.value)} />
            </React.Fragment>
        );
    }

    return (
        <>
            {hideNavbar ? null : <AdaptiveNavBar mobilePresent isErrorPageHeader={true} />}
            {tutorialCurrentPage === 1 ? (
                <PageContent
                    Icon={WelcomeImage}
                    stylesOverride={stylesOverride}
                    bodyTestDataAttr={bodyTestDataAttr}
                    subtitleText={subtitleText}
                    bodyText={bodyText}
                />
            ) : (
                <>
                    <Progress value={tutorialCurrentPage} />
                    {type === TUTORIAL_TYPE.CALENDAR && <CalendarTutorial tutorialCurrentPage={tutorialCurrentPage} isMobile={isMobile} type={type} />}
                    {type === TUTORIAL_TYPE.ONBOARDING && <OnboardingTutorial tutorialCurrentPage={tutorialCurrentPage} isMobile={isMobile} type={type} />}
                </>
            )}
            <Footer
                isNextButtonDisabled={false}
                isBackButtonDisabled={tutorialCurrentPage === 1 ? true : false}
                onNextClick={onNextPage}
                onBackClick={onPreviousPage}
                nextButtonText={tutorialCurrentPage === 1 ? buttonText : tutorialCurrentPage === noOfPages ? 'Done' : 'Next'}
                backButtonText={'Back'}
                isMobile={isMobile}
            />
        </>
    );
}

/* type checking for props */
TutorialWelcomePage.propTypes = {
    bodyText: PropTypes.string.isRequired,
    buttonText: PropTypes.string,
    setLaunchTutorial: PropTypes.func,
    bodyTestDataAttr: PropTypes.string,
    stylesOverride: PropTypes.any,
};

const PageContent = ({ Icon, iconTextDataAttr, subtitleText, bodyText, bodyTestDataAttr, stylesOverride }) => {
    const classes = TutorialPageStyle(stylesOverride)();

    return (
        <div className={classes.contentLayoutDiv}>
            <Box className={classes.root}>
                <div className={classes.body}>
                    {Icon && <Icon data-cy={iconTextDataAttr} aria-labelledby="icon" className={classes.icon} />}
                    <Typography variant="h2" className={classes.subtitle} data-cy="tutorial_subtitle_text">
                        {subtitleText}
                    </Typography>
                    <Typography data-cy={bodyTestDataAttr} variant="body1" className={classes.bodyText}>
                        {bodyText}
                    </Typography>
                </div>
            </Box>
        </div>
    );
};

const Footer = ({ isNextButtonDisabled, isBackButtonDisabled, onNextClick, onBackClick, nextButtonText, backButtonText, isMobile, ...otherButtonProps }) => {
    const classes = useStyles();
    const FooterContent = ({ className }) => (
        <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-around"
            width="100%"
            position="fixed"
            bottom="0px"
            className={`${className ? className : ''}`}
        >
            {!isBackButtonDisabled && (
                <Grid className={classes.buttonGrid} style={isMobile ? {} : { justifyContent: 'left' }}>
                    <PrimaryButton
                        disabled={isBackButtonDisabled}
                        onClick={() => {
                            onBackClick();
                        }}
                        variant="outlined"
                        disableElevation
                        className={classes.backButton}
                        component="span"
                    >
                        {backButtonText}
                    </PrimaryButton>
                </Grid>
            )}
            <Grid className={classes.buttonGrid}>
                <PrimaryButton
                    disabled={isNextButtonDisabled}
                    onClick={() => {
                        onNextClick();
                    }}
                    className={classes.nextButton}
                    component="span"
                >
                    {nextButtonText}
                </PrimaryButton>
            </Grid>
            {!isBackButtonDisabled && !isMobile && <Grid className={classes.buttonGrid} />}
        </Box>
    );

    const Mobile = <FooterContent className={classes.mobileFooter} />;
    const Desktop = <FooterContent className={classes.whiteBackground} />;

    return <SizeSelector mobile={Mobile} desktop={Desktop} />;
};
